import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useLanguage } from '../../contexts/LanguageContext';
import { useSalon } from '../../contexts/SalonContext';
import ChangeLog from './ChangeLog';
import useScrollLock from '../../hooks/useScrollLock';
import { processImages, isImageFile, validateFileSize, MAX_IMAGE_MB, MAX_VIDEO_MB, formatSizeLimit } from '../../utils/imageProcessor';
import { getImageUrl, getThumbnailUrl } from '../../utils/api';
import videoProcessor from '../../utils/videoProcessor';
import { VideoTranscodingModal } from '../common';
import ColorSelect from './ColorSelect';
import {
  CARE_WASHING_OPTIONS,
  CARE_BLEACHING_OPTIONS,
  CARE_DRYING_OPTIONS,
  CARE_IRONING_OPTIONS,
  CARE_DRY_CLEANING_OPTIONS,
  getCareSelectOptions,
  DEFAULT_DRESS_CARE
} from '../../utils/dressCare';
import './Salon.css';

function getImageDragId(img) {
  if (img.clientId) return `img-${img.clientId}`;
  return `img-path:${img.preview}`;
}

function isImageDragId(id) {
  return id.startsWith('img-path:') || (id.startsWith('img-') && !id.startsWith('img-path:'));
}

function SortableImageThumb({ id, image, index, onRemove, getThumbnailUrl }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.65 : 1,
    zIndex: isDragging ? 2 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`image-preview-item sortable-media-thumb ${isDragging ? 'is-dragging' : ''}`}
      {...attributes}
      {...listeners}
    >
      <img
        src={image.isNew ? image.preview : getThumbnailUrl(image.preview)}
        alt={`Preview ${index + 1}`}
        draggable={false}
      />
      <button
        type="button"
        className="remove-image"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onRemove(index);
        }}
        aria-label="Remove image"
      >
        ×
      </button>
    </div>
  );
}

function SortableVideoThumb({ id, video, onRemove, label }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.65 : 1,
    zIndex: isDragging ? 2 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`image-preview-item video-preview-item sortable-media-thumb ${isDragging ? 'is-dragging' : ''}`}
      {...attributes}
      {...listeners}
    >
      <video
        src={video.preview}
        controls
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
      <button
        type="button"
        className="remove-image"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        aria-label={label}
      >
        ×
      </button>
    </div>
  );
}

const AddEditDressModal = ({ isOpen, onClose, onSuccess, dress, salonId, brands, userRole }) => {
  const { t } = useLanguage();
  const { currencySymbol } = useSalon();
  const modalBodyRef = useRef(null);
  const originalDressRef = useRef(null);
  const [isChangeLogVisible, setIsChangeLogVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    rental_price: '',
    selling_price: '',
    arrival_date: '',
    brand_id: '',
    video_url: '',
    video2_url: '',
    size: '',
    colour: '',
    notes: '',
    stock_count: 1,
    item_code: '',
    country_of_origin: '',
    manufacturer: '',
    importer: '',
    maintenance_procedure: '',
    material_composition: '',
    shrinkage_during_care: '',
    ...DEFAULT_DRESS_CARE,
    images: []
  });
  const [uploadedImages, setUploadedImages] = useState([]);
  const [uploadedVideo, setUploadedVideo] = useState(null);
  const [uploadedVideo2, setUploadedVideo2] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  // Video transcoding state
  const [isTranscoding, setIsTranscoding] = useState(false);
  const [transcodingProgress, setTranscodingProgress] = useState(0);
  const [transcodingStatus, setTranscodingStatus] = useState('preparing');
  const [transcodingError, setTranscodingError] = useState(null);
  const [originalVideoFile, setOriginalVideoFile] = useState(null);
  
  // Video2 transcoding state
  const [isTranscoding2, setIsTranscoding2] = useState(false);
  const [transcodingProgress2, setTranscodingProgress2] = useState(0);
  const [transcodingStatus2, setTranscodingStatus2] = useState('preparing');
  const [transcodingError2, setTranscodingError2] = useState(null);
  const [originalVideoFile2, setOriginalVideoFile2] = useState(null);

  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  // Scroll to top when errors are displayed
  useEffect(() => {
    if (Object.keys(errors).length > 0 && modalBodyRef.current) {
      modalBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [errors]);

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      // Reset video processor when component unmounts
      videoProcessor.reset().catch(error => {
        console.error('Error resetting video processor on unmount:', error);
      });
    };
  }, []);

  // Helper function to format date for HTML date input
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    
    try {
      // If it's already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      
      // If it's an ISO string or other format, convert to YYYY-MM-DD
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error formatting date for input:', error);
      return '';
    }
  };

  // Convert database field name to localized label for dresses
  const getLocalizedFieldName = (fieldName) => {
    const fieldNameMap = {
      'name': 'dressName',
      'brand_id': 'brand',
      'rental_price': 'rentalPrice',
      'selling_price': 'sellingPrice',
      'arrival_date': 'arrivalDate',
      'size': 'size',
      'colour': 'color',
      'notes': 'notes',
      'stock_count': 'stockCount',
      'item_code': 'itemCode',
      'country_of_origin': 'countryOfOrigin',
      'manufacturer': 'manufacturer',
      'importer': 'importer',
      'maintenance_procedure': 'maintenanceProcedure',
      'material_composition': 'materialComposition',
      'shrinkage_during_care': 'shrinkageDuringCare',
      'care_washing': 'careWashingField',
      'care_bleaching': 'careBleachingField',
      'care_drying': 'careDryingField',
      'care_ironing': 'careIroningField',
      'care_dry_cleaning': 'careDryCleaningField',
      'video_url': 'video',
      'video2_url': 'video'
    };

    const translationKey = fieldNameMap[fieldName];
    if (translationKey) {
      return t(`dresses.${translationKey}`);
    }
    
    // Fallback: convert snake_case to Title Case
    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  useEffect(() => {
    if (isOpen) {
      setIsChangeLogVisible(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setErrors({});
      return;
    }
    if (dress) {
      console.log('Edit mode - dress data:', dress);
      console.log('Arrival date:', dress.arrival_date);
      
      const dressData = {
        name: dress.name || '',
        rental_price: dress.rental_price || '',
        selling_price: dress.selling_price || '',
        arrival_date: formatDateForInput(dress.arrival_date),
        brand_id: dress.brand_id || '',
        video_url: dress.video_url || '',
        video2_url: dress.video2_url || '',
        size: dress.size || '',
        colour: dress.colour || '',
        notes: dress.notes || '',
        stock_count: dress.stock_count ?? 1,
        item_code: dress.item_code || '',
        country_of_origin: dress.country_of_origin || '',
        manufacturer: dress.manufacturer || '',
        importer: dress.importer || '',
        maintenance_procedure: dress.maintenance_procedure || '',
        material_composition: dress.material_composition || '',
        shrinkage_during_care: dress.shrinkage_during_care || '',
        care_washing: dress.care_washing || '',
        care_bleaching: dress.care_bleaching || '',
        care_drying: dress.care_drying || '',
        care_ironing: dress.care_ironing || '',
        care_dry_cleaning: dress.care_dry_cleaning || '',
        images: dress.images || []
      };
      
      setFormData(dressData);
      originalDressRef.current = dressData;
      // Transform existing images into the expected format
      const existingImages = (dress.images || []).map(imagePath => ({
        preview: imagePath,
        isNew: false,
        clientId: null,
      }));
      setUploadedImages(existingImages);
      
      // Set existing videos if available
      if (dress.video_url) {
        setUploadedVideo({
          preview: getImageUrl(dress.video_url), // Convert relative path to full URL
          isNew: false
        });
      } else {
        setUploadedVideo(null);
      }
      
      if (dress.video2_url) {
        setUploadedVideo2({
          preview: getImageUrl(dress.video2_url), // Convert relative path to full URL
          isNew: false
        });
      } else {
        setUploadedVideo2(null);
      }
    } else {
      setFormData({
        name: '',
        rental_price: '',
        selling_price: '',
        arrival_date: '',
        brand_id: '',
        video_url: '',
        video2_url: '',
        size: '',
        colour: '',
        notes: '',
        stock_count: 1,
        item_code: '',
        country_of_origin: '',
        manufacturer: '',
        importer: '',
        maintenance_procedure: '',
        material_composition: '',
        shrinkage_during_care: '',
        ...DEFAULT_DRESS_CARE,
        images: []
      });
      setUploadedImages([]);
      setUploadedVideo(null);
      setUploadedVideo2(null);
      originalDressRef.current = null;
    }
    
    // Clear errors when modal opens/closes
    setErrors({});
    // Only re-hydrate when modal opens or a different dress is selected — not when `dress`
    // is a new object reference for the same id (otherwise removing a video is undone).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dress fields read when id/isOpen change
  }, [isOpen, dress?.id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    // Check if adding these files would exceed the limit
    if (uploadedImages.length + files.length > 10) {
      setErrors(prev => ({ ...prev, images: `Maximum of 10 images allowed. You currently have ${uploadedImages.length} images.` }));
      return;
    }
    
    // Validate files
    for (const file of files) {
      if (!isImageFile(file)) {
        setErrors(prev => ({ ...prev, images: t('messages.error.onlyImageFilesAllowed') }));
        return;
      }
      if (!validateFileSize(file)) {
        setErrors(prev => ({ ...prev, images: t('messages.error.fileSizeMustBeLessThan', '', { size: formatSizeLimit(MAX_IMAGE_MB) }) }));
        return;
      }
    }
    
    try {
      // Process images (resize and compress)
      const processedFiles = await processImages(files);
      
      const newImages = processedFiles.map(file => ({
        file,
        preview: URL.createObjectURL(file),
        isNew: true,
        clientId: crypto.randomUUID(),
      }));
      
      setUploadedImages(prev => [...prev, ...newImages]);
      setErrors(prev => ({ ...prev, images: '' })); // Clear any previous errors
    } catch (error) {
      console.error('Error processing images:', error);
      setErrors(prev => ({ ...prev, images: t('messages.error.failedToProcessImages') }));
    }
    
    // Clear the input so the same files can be selected again if needed
    e.target.value = '';
  };

  const removeImage = (index) => {
    const imageToRemove = uploadedImages[index];
    
    // If it's a new upload, revoke the object URL
    if (imageToRemove.isNew) {
      URL.revokeObjectURL(imageToRemove.preview);
    }
    
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    
    if (!file) return;
    
    // Check if both videos are already uploaded
    if (uploadedVideo && uploadedVideo2) {
      setErrors(prev => ({ ...prev, video: 'Maximum of 2 videos allowed' }));
      return;
    }
    
    // Determine which video slot to fill
    const shouldFillVideo2 = uploadedVideo && !uploadedVideo2;
    const errorKey = shouldFillVideo2 ? 'video2' : 'video';
    
    // Validate file type - check both MIME type and file extension
    const allowedMimeTypes = [
      'video/mp4', 'video/avi', 'video/mov', 'video/quicktime', 
      'video/wmv', 'video/flv', 'video/webm', 'video/mkv',
      'video/x-msvideo', 'video/x-ms-wmv'
    ];
    
    const allowedExtensions = ['.mp4', '.avi', '.mov', '.wmv', '.flv', '.webm', '.mkv'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    
    const isValidMimeType = allowedMimeTypes.includes(file.type);
    const isValidExtension = allowedExtensions.includes(fileExtension);
    
    if (!isValidMimeType && !isValidExtension) {
      setErrors(prev => ({ ...prev, [errorKey]: 'Only video files (MP4, AVI, MOV, WMV, FLV, WEBM, MKV) are allowed' }));
      return;
    }
    
    // Validate file size (the original - only the transcoded, much smaller file is uploaded)
    if (!validateFileSize(file, MAX_VIDEO_MB)) {
      setErrors(prev => ({ ...prev, [errorKey]: t('messages.error.fileSizeMustBeLessThan', '', { size: formatSizeLimit(MAX_VIDEO_MB) }) }));
      return;
    }
    
    // Clear any previous errors
    setErrors(prev => ({ ...prev, [errorKey]: '' }));
    
    // Route to appropriate video handler
    if (shouldFillVideo2) {
      setOriginalVideoFile2(file);
      await startVideo2Transcoding(file);
    } else {
      setOriginalVideoFile(file);
      await startVideoTranscoding(file);
    }
    
    // Clear the input so the same file can be selected again if needed
    e.target.value = '';
  };

  const startVideoTranscoding = async (file) => {
    try {
      setIsTranscoding(true);
      setTranscodingStatus('preparing');
      setTranscodingProgress(0);
      setTranscodingError(null);

      // Set up video processor callbacks
      videoProcessor.setProgressCallback((progress) => {
        setTranscodingProgress(progress);
        setTranscodingStatus('processing');
      });

      videoProcessor.setCompleteCallback((transcodedBlob) => {
        // Create a new File object from the transcoded blob
        const transcodedFile = new File([transcodedBlob], file.name.replace(/\.[^/.]+$/, '.mp4'), {
          type: 'video/mp4'
        });

        // Create preview with transcoded file
        const videoPreview = {
          file: transcodedFile,
          preview: URL.createObjectURL(transcodedBlob),
          isNew: true,
          isTranscoded: true
        };

        setUploadedVideo(videoPreview);
        setTranscodingStatus('completed');
        
        // Auto-close transcoding modal after a short delay
        setTimeout(() => {
          setIsTranscoding(false);
        }, 2000);
      });

      videoProcessor.setErrorCallback((error) => {
        console.error('Video transcoding error:', error);
        setTranscodingError(error.message || 'Transcoding failed');
        setTranscodingStatus('error');
      });

      // Start transcoding
      await videoProcessor.transcodeVideo(file, {
        bitrate: '10M' // 10 Mbps
      });

    } catch (error) {
      console.error('Error starting video transcoding:', error);
      setTranscodingError(error.message || t('messages.error.failedToStartTranscoding'));
      setTranscodingStatus('error');
    }
  };

  const cancelVideoTranscoding = async () => {
    try {
      await videoProcessor.cancel();
      setTranscodingStatus('cancelled');
      setIsTranscoding(false);
      setOriginalVideoFile(null);
    } catch (error) {
      console.error('Error cancelling transcoding:', error);
    }
  };


  const startVideo2Transcoding = async (file) => {
    try {
      setIsTranscoding2(true);
      setTranscodingStatus2('preparing');
      setTranscodingProgress2(0);
      setTranscodingError2(null);

      // Set up video processor callbacks
      videoProcessor.setProgressCallback((progress) => {
        setTranscodingProgress2(progress);
        setTranscodingStatus2('processing');
      });

      videoProcessor.setCompleteCallback((transcodedBlob) => {
        // Create a new File object from the transcoded blob
        const transcodedFile = new File([transcodedBlob], file.name.replace(/\.[^/.]+$/, '.mp4'), {
          type: 'video/mp4'
        });

        // Create preview with transcoded file
        const videoPreview = {
          file: transcodedFile,
          preview: URL.createObjectURL(transcodedBlob),
          isNew: true,
          isTranscoded: true
        };

        setUploadedVideo2(videoPreview);
        setTranscodingStatus2('completed');
        
        // Auto-close transcoding modal after a short delay
        setTimeout(() => {
          setIsTranscoding2(false);
        }, 2000);
      });

      videoProcessor.setErrorCallback((error) => {
        console.error('Video2 transcoding error:', error);
        setTranscodingError2(error.message || 'Transcoding failed');
        setTranscodingStatus2('error');
      });

      // Start transcoding
      await videoProcessor.transcodeVideo(file, {
        bitrate: '10M' // 10 Mbps
      });

    } catch (error) {
      console.error('Error starting video2 transcoding:', error);
      setTranscodingError2(error.message || t('messages.error.failedToStartTranscoding'));
      setTranscodingStatus2('error');
    }
  };

  const cancelVideo2Transcoding = async () => {
    try {
      await videoProcessor.cancel();
      setTranscodingStatus2('cancelled');
      setIsTranscoding2(false);
      setOriginalVideoFile2(null);
    } catch (error) {
      console.error('Error cancelling video2 transcoding:', error);
    }
  };

  const removeVideo = () => {
    if (uploadedVideo && uploadedVideo.isNew) {
      URL.revokeObjectURL(uploadedVideo.preview);
    }
    setUploadedVideo(null);
    setFormData((prev) => ({ ...prev, video_url: '' }));
  };

  const removeVideo2 = () => {
    if (uploadedVideo2 && uploadedVideo2.isNew) {
      URL.revokeObjectURL(uploadedVideo2.preview);
    }
    setUploadedVideo2(null);
    setFormData((prev) => ({ ...prev, video2_url: '' }));
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const imageSortableIds = useMemo(
    () => uploadedImages.map(getImageDragId),
    [uploadedImages]
  );

  const handleMediaDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const aid = String(active.id);
    const oid = String(over.id);

    if (isImageDragId(aid)) {
      if (!isImageDragId(oid)) return;
      setUploadedImages((items) => {
        const oldIndex = items.findIndex((img) => getImageDragId(img) === aid);
        const newIndex = items.findIndex((img) => getImageDragId(img) === oid);
        if (oldIndex === -1 || newIndex === -1) return items;
        return arrayMove(items, oldIndex, newIndex);
      });
      return;
    }

    if (
      (aid === 'video-1' && oid === 'video-2') ||
      (aid === 'video-2' && oid === 'video-1')
    ) {
      const v1 = uploadedVideo;
      const v2 = uploadedVideo2;
      if (!v1 || !v2) return;
      setUploadedVideo(v2);
      setUploadedVideo2(v1);
      setFormData((prev) => ({
        ...prev,
        video_url: prev.video2_url,
        video2_url: prev.video_url,
      }));
    }
  };

  const handleClose = async () => {
    // Reset video processor when modal is closed
    try {
      await videoProcessor.reset();
    } catch (error) {
      console.error('Error resetting video processor on close:', error);
    }
    onClose();
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('messages.error.dressNameRequired');
    }
    
    if (!formData.rental_price || formData.rental_price <= 0) {
      newErrors.rental_price = t('messages.error.validRentalPriceRequired');
    }
    
    if (!formData.selling_price || formData.selling_price <= 0) {
      newErrors.selling_price = t('messages.error.validSellingPriceRequired');
    }
    
    if (!formData.arrival_date) {
      newErrors.arrival_date = t('messages.error.arrivalDateRequired');
    }
    
    if (!formData.brand_id) {
      newErrors.brand_id = t('messages.error.brandSelectionRequired');
    }
    
    // Validate size if provided
    if (formData.size && (isNaN(formData.size) || formData.size <= 0 || formData.size > 60)) {
      newErrors.size = t('messages.error.sizeMustBeBetween1And60');
    }
    
    // Validate colour if provided
    const validColours = ['ivory', 'white', 'champagne', 'blush', 'black', 'blue', 'gold', 'pink', 'purple', 'red', 'green', 'yellow', 'gray', 'orange', 'mint', 'mocha'];
    if (formData.colour && !validColours.includes(formData.colour)) {
      newErrors.colour = t('messages.error.pleaseSelectValidColour');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const logDressChanges = async (originalDress, updatedData) => {
    if (!originalDress || !dress?.id) {
      console.log('logDressChanges: Skipping - no originalDress or dress.id');
      return;
    }

    console.log('logDressChanges: Starting', { 
      dressId: dress.id,
      originalKeys: Object.keys(originalDress),
      updatedKeys: Object.keys(updatedData)
    });

    const trackedFields = [
      'name',
      'brand_id',
      'rental_price',
      'selling_price',
      'arrival_date',
      'size',
      'colour',
      'notes',
      'stock_count',
      'item_code',
      'country_of_origin',
      'manufacturer',
      'importer',
      'maintenance_procedure',
      'material_composition',
      'shrinkage_during_care',
      'care_washing',
      'care_bleaching',
      'care_drying',
      'care_ironing',
      'care_dry_cleaning',
      'video_url',
      'video2_url'
    ];

    const sanitizeValue = (value) => {
      if (value === null || value === undefined || value === '') return '';
      if (typeof value === 'number') return String(value);
      return String(value).trim();
    };

    const changes = trackedFields.reduce((acc, field) => {
      const previousValue = sanitizeValue(originalDress[field]);
      const newValue = sanitizeValue(updatedData[field]);
      if (previousValue !== newValue) {
        acc.push({
          field_name: field,
          previous_value: previousValue || null,
          new_value: newValue || null
        });
      }
      return acc;
    }, []);

    console.log('logDressChanges: Found changes', changes);

    if (changes.length === 0) {
      console.log('logDressChanges: No changes detected');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('logDressChanges: No token found');
      return;
    }

    try {
      const results = await Promise.all(
        changes.map(change =>
          axios.post(
            `/api/salons/${salonId}/change-logs`,
            {
              entity_type: 'dress',
              entity_id: dress.id,
              field_name: change.field_name,
              previous_value: change.previous_value,
              new_value: change.new_value,
              change_type: 'update',
              metadata: {
                dress_id: dress.id
              }
            },
            {
              headers: {
                Authorization: `Bearer ${token}`
              }
            }
          ).then(response => {
            console.log('logDressChanges: Successfully logged change', {
              field: change.field_name,
              response: response.data
            });
            return response;
          }).catch(error => {
            console.error('Error logging dress change:', {
              field: change.field_name,
              error: error.response?.data || error.message,
              status: error.response?.status
            });
            throw error;
          })
        )
      );
      console.log('logDressChanges: All changes logged successfully', results);
    } catch (error) {
      console.error('logDressChanges: Error logging changes', error);
    }

    originalDressRef.current = {
      ...originalDress,
      ...updatedData
    };
  };

  const handleToggleChangeLog = () => {
    if (!dress?.id) return;
    setIsChangeLogVisible(!isChangeLogVisible);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      
      // Add form fields
      formDataToSend.append('name', formData.name);
      formDataToSend.append('rental_price', formData.rental_price);
      formDataToSend.append('selling_price', formData.selling_price);
      formDataToSend.append('arrival_date', formData.arrival_date);
      formDataToSend.append('brand_id', formData.brand_id);
      formDataToSend.append('video_url', formData.video_url);
      formDataToSend.append('video2_url', formData.video2_url);
      formDataToSend.append('size', formData.size || '');
      formDataToSend.append('colour', formData.colour || '');
      formDataToSend.append('notes', formData.notes || '');
      formDataToSend.append('stock_count', formData.stock_count ?? 1);
      formDataToSend.append('item_code', formData.item_code || '');
      formDataToSend.append('country_of_origin', formData.country_of_origin || '');
      formDataToSend.append('manufacturer', formData.manufacturer || '');
      formDataToSend.append('importer', formData.importer || '');
      formDataToSend.append('maintenance_procedure', formData.maintenance_procedure || '');
      formDataToSend.append('material_composition', formData.material_composition || '');
      formDataToSend.append('shrinkage_during_care', formData.shrinkage_during_care || '');
      formDataToSend.append('care_washing', formData.care_washing || '');
      formDataToSend.append('care_bleaching', formData.care_bleaching || '');
      formDataToSend.append('care_drying', formData.care_drying || '');
      formDataToSend.append('care_ironing', formData.care_ironing || '');
      formDataToSend.append('care_dry_cleaning', formData.care_dry_cleaning || '');
      
      // Add video files if uploaded
      if (uploadedVideo && uploadedVideo.isNew) {
        formDataToSend.append('video', uploadedVideo.file);
      }
      
      if (uploadedVideo2 && uploadedVideo2.isNew) {
        formDataToSend.append('video2', uploadedVideo2.file);
      }
      
      // Add existing images (not new uploads)
      const existingImages = uploadedImages.filter(img => !img.isNew);
      formDataToSend.append('existing_images', JSON.stringify(existingImages.map(img => img.preview)));
      
      // Add new image files
      const newImageFiles = uploadedImages.filter(img => img.isNew);
      newImageFiles.forEach(img => {
        formDataToSend.append('images', img.file);
      });
      
      const url = dress 
        ? `/api/salons/${salonId}/dresses/${dress.id}`
        : `/api/salons/${salonId}/dresses`;
      
      const method = dress ? 'PUT' : 'POST';
      
      const response = await axios({
        method,
        url,
        data: formDataToSend,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.status === 200 || response.status === 201) {
        if (dress) {
          await logDressChanges(
            originalDressRef.current || {
              name: dress.name || '',
              rental_price: dress.rental_price || '',
              selling_price: dress.selling_price || '',
              arrival_date: formatDateForInput(dress.arrival_date),
              brand_id: dress.brand_id || '',
              video_url: dress.video_url || '',
              video2_url: dress.video2_url || '',
              size: dress.size || '',
              colour: dress.colour || '',
              notes: dress.notes || '',
              stock_count: dress.stock_count ?? 1,
              item_code: dress.item_code || '',
              country_of_origin: dress.country_of_origin || '',
              manufacturer: dress.manufacturer || '',
              importer: dress.importer || '',
              maintenance_procedure: dress.maintenance_procedure || '',
              material_composition: dress.material_composition || '',
              shrinkage_during_care: dress.shrinkage_during_care || '',
              care_washing: dress.care_washing || '',
              care_bleaching: dress.care_bleaching || '',
              care_drying: dress.care_drying || '',
              care_ironing: dress.care_ironing || '',
              care_dry_cleaning: dress.care_dry_cleaning || ''
            },
            {
              name: formData.name,
              rental_price: formData.rental_price,
              selling_price: formData.selling_price,
              arrival_date: formData.arrival_date,
              brand_id: formData.brand_id,
              video_url: formData.video_url,
              video2_url: formData.video2_url,
              size: formData.size,
              colour: formData.colour,
              notes: formData.notes,
              stock_count: formData.stock_count,
              item_code: formData.item_code,
              country_of_origin: formData.country_of_origin,
              manufacturer: formData.manufacturer,
              importer: formData.importer,
              maintenance_procedure: formData.maintenance_procedure,
              material_composition: formData.material_composition,
              shrinkage_during_care: formData.shrinkage_during_care,
              care_washing: formData.care_washing,
              care_bleaching: formData.care_bleaching,
              care_drying: formData.care_drying,
              care_ironing: formData.care_ironing,
              care_dry_cleaning: formData.care_dry_cleaning
            }
          );
        }
        onSuccess();
      }
    } catch (error) {
      console.error('Error saving dress:', error);
      alert(t('messages.error.failedToSaveDress'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>{dress ? t('dresses.editDress') : t('dresses.addDress')}</h3>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>
        
        <div className="modal-body" ref={modalBodyRef}>
          <form onSubmit={handleSubmit} className="base-form">
          {dress && (
            <div className="form-row">
              <div className="form-group">
                <label>{t('dresses.dressId')}</label>
                <input
                  type="text"
                  value={dress.id}
                  disabled
                  readOnly
                  style={{
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed',
                    color: '#666'
                  }}
                />
              </div>
            </div>
          )}
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="name">{t('dresses.dressName')} *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={errors.name ? 'error' : ''}
                placeholder={t('dresses.dressName')}
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="brand_id">{t('dresses.brand')} *</label>
              <select
                id="brand_id"
                name="brand_id"
                value={formData.brand_id}
                onChange={handleInputChange}
                className={errors.brand_id ? 'error' : ''}
              >
                <option value="">{t('dresses.selectBrand')}</option>
                {brands.map(brand => (
                  <option key={brand.id} value={brand.id}>
                    {brand.brand_name}
                  </option>
                ))}
              </select>
              {errors.brand_id && <span className="error-message">{errors.brand_id}</span>}
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="rental_price">{t('dresses.rentalPrice')} ({currencySymbol}) *</label>
              <input
                type="number"
                id="rental_price"
                name="rental_price"
                value={formData.rental_price}
                onChange={handleInputChange}
                className={errors.rental_price ? 'error' : ''}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
              {errors.rental_price && <span className="error-message">{errors.rental_price}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="selling_price">{t('dresses.sellingPrice')} ({currencySymbol}) *</label>
              <input
                type="number"
                id="selling_price"
                name="selling_price"
                value={formData.selling_price}
                onChange={handleInputChange}
                className={errors.selling_price ? 'error' : ''}
                placeholder="0.00"
                step="0.01"
                min="0"
              />
              {errors.selling_price && <span className="error-message">{errors.selling_price}</span>}
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="arrival_date">{t('dresses.arrivalDate')} *</label>
              <div className="date-field-wrap">
                <input
                  type="date"
                  id="arrival_date"
                  name="arrival_date"
                  value={formData.arrival_date}
                  onChange={handleInputChange}
                  className={errors.arrival_date ? 'error' : ''}
                />
              </div>
              {errors.arrival_date && <span className="error-message">{errors.arrival_date}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="stock_count">{t('dresses.stockCount')}</label>
              <input
                type="number"
                id="stock_count"
                name="stock_count"
                min="0"
                value={formData.stock_count}
                onChange={handleInputChange}
                className={errors.stock_count ? 'error' : ''}
              />
              {errors.stock_count && <span className="error-message">{errors.stock_count}</span>}
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="size">{t('dresses.size')}</label>
              <input
                type="number"
                id="size"
                name="size"
                value={formData.size}
                onChange={handleInputChange}
                className={errors.size ? 'error' : ''}
                placeholder="e.g., 10"
                min="1"
                max="60"
              />
              {errors.size && <span className="error-message">{errors.size}</span>}
            </div>
            
            <div className="form-group">
              <label htmlFor="colour">{t('dresses.color')}</label>
              <ColorSelect
                value={formData.colour}
                onChange={handleInputChange}
                error={errors.colour}
              />
              {errors.colour && <span className="error-message">{errors.colour}</span>}
            </div>
          </div>
          
          <div className="form-row">
            <div className="form-group">
              <label>{t('dresses.images')}</label>
              <div className="image-upload-section">
                <input
                  type="file"
                  id="images"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="file-input"
                  disabled={uploadedImages.length >= 10}
                />
                <label 
                  htmlFor="images" 
                  className={`file-input-label ${uploadedImages.length >= 10 ? 'disabled' : ''}`}
                >
                  <span className="upload-icon">📷</span>
                  {uploadedImages.length >= 10 ? t('dresses.maxImagesReached') : 
                   uploadedImages.length > 0 ? `${t('dresses.uploadImages')} (${uploadedImages.length}/10)` : 
                   t('dresses.uploadImages')}
                </label>
                <p className="file-input-help">
                  {uploadedImages.length >= 10 ? 
                    t('dresses.maxImagesAllowed') : 
                    `${t('dresses.uploadUpTo')} 10 ${t('dresses.images').toLowerCase()} (${uploadedImages.length}/10 ${t('dresses.used')}, 9MB ${t('dresses.maxEach')})`
                  }
                </p>
              </div>
              {errors.images && <span className="error-message">{errors.images}</span>}
            </div>
            
            <div className="form-group">
              <label>{t('dresses.videos')}</label>
              <div className="video-upload-section">
                <input
                  type="file"
                  id="video"
                  accept="video/*"
                  onChange={handleVideoUpload}
                  className="file-input"
                  disabled={uploadedVideo && uploadedVideo2}
                />
                <label 
                  htmlFor="video" 
                  className={`file-input-label ${uploadedVideo && uploadedVideo2 ? 'disabled' : ''}`}
                >
                  <span className="upload-icon">🎥</span>
                  {uploadedVideo && uploadedVideo2 ? t('dresses.bothVideosUploaded') : 
                   uploadedVideo ? t('dresses.chooseVideo2') : 
                   uploadedVideo2 ? `${t('dresses.chooseVideo')} 1` : 
                   t('dresses.chooseVideo')}
                </label>
                <p className="file-input-help">
                  {uploadedVideo && uploadedVideo2 ? 
                    t('dresses.maxVideosAllowed') : 
                    t('dresses.videoUploadHelp')
                  }
                </p>
              </div>
              
              {errors.video && <span className="error-message">{errors.video}</span>}
              {errors.video2 && <span className="error-message">{errors.video2}</span>}
            </div>
          </div>
          
          {(uploadedImages.length > 0 || uploadedVideo || uploadedVideo2) && (
            <div className="form-group">
              <div className="image-preview-grid">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleMediaDragEnd}
                >
                  <>
                    <SortableContext items={imageSortableIds} strategy={rectSortingStrategy}>
                      {uploadedImages.map((image, index) => (
                        <SortableImageThumb
                          key={getImageDragId(image)}
                          id={getImageDragId(image)}
                          image={image}
                          index={index}
                          onRemove={removeImage}
                          getThumbnailUrl={getThumbnailUrl}
                        />
                      ))}
                    </SortableContext>
                    {uploadedVideo && uploadedVideo2 ? (
                      <SortableContext
                        items={['video-1', 'video-2']}
                        strategy={rectSortingStrategy}
                      >
                        <SortableVideoThumb
                          id="video-1"
                          video={uploadedVideo}
                          onRemove={removeVideo}
                          label="Remove video 1"
                        />
                        <SortableVideoThumb
                          id="video-2"
                          video={uploadedVideo2}
                          onRemove={removeVideo2}
                          label="Remove video 2"
                        />
                      </SortableContext>
                    ) : (
                      <>
                        {uploadedVideo && (
                          <div className="image-preview-item video-preview-item">
                            <video
                              src={uploadedVideo.preview}
                              controls
                              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                            <button
                              type="button"
                              className="remove-image"
                              onClick={removeVideo}
                            >
                              ×
                            </button>
                          </div>
                        )}
                        {uploadedVideo2 && (
                          <div className="image-preview-item video-preview-item">
                            <video
                              src={uploadedVideo2.preview}
                              controls
                              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                            <button
                              type="button"
                              className="remove-image"
                              onClick={removeVideo2}
                            >
                              ×
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </>
                </DndContext>
              </div>
              <p className="media-drag-hint">{t('dresses.mediaDragHint')}</p>
            </div>
          )}

          <div className="form-section dress-care-section dress-product-details">
            <h4 className="form-section-title">{t('dresses.productDetails', 'Product details')}</h4>
            <div className="form-row form-row-two-cols">
              <div className="form-group">
                <label htmlFor="item_code">{t('dresses.itemCode', 'Item code')}</label>
                <input
                  type="text"
                  id="item_code"
                  name="item_code"
                  value={formData.item_code}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="country_of_origin">{t('dresses.countryOfOrigin', 'Country of origin')}</label>
                <input
                  type="text"
                  id="country_of_origin"
                  name="country_of_origin"
                  value={formData.country_of_origin}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="form-row form-row-two-cols">
              <div className="form-group">
                <label htmlFor="manufacturer">{t('dresses.manufacturer', 'Manufacturer')}</label>
                <input
                  type="text"
                  id="manufacturer"
                  name="manufacturer"
                  value={formData.manufacturer}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="importer">{t('dresses.importer', 'Importer')}</label>
                <input
                  type="text"
                  id="importer"
                  name="importer"
                  value={formData.importer}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="form-row form-row-two-cols">
              <div className="form-group">
                <label htmlFor="material_composition">{t('dresses.materialComposition', 'Material composition')}</label>
                <input
                  type="text"
                  id="material_composition"
                  name="material_composition"
                  value={formData.material_composition}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-group">
                <label htmlFor="shrinkage_during_care">{t('dresses.shrinkageDuringCare', 'Shrinkage during care')}</label>
                <input
                  type="text"
                  id="shrinkage_during_care"
                  name="shrinkage_during_care"
                  value={formData.shrinkage_during_care}
                  onChange={handleInputChange}
                />
              </div>
            </div>
            <div className="form-row form-row-two-cols">
              <div className="form-group form-group-full">
                <label htmlFor="maintenance_procedure">{t('dresses.maintenanceProcedure', 'Maintenance procedure')}</label>
                <input
                  type="text"
                  id="maintenance_procedure"
                  name="maintenance_procedure"
                  value={formData.maintenance_procedure}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          </div>

          <div className="form-section dress-care-section">
            <h4 className="form-section-title">{t('dresses.careInstructions', 'Care instructions')}</h4>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="care_washing">{t('dresses.careWashingField', 'Washing')}</label>
                <select
                  id="care_washing"
                  name="care_washing"
                  value={formData.care_washing}
                  onChange={handleInputChange}
                >
                  <option value="">{t('dresses.selectCareOption', '—')}</option>
                  {getCareSelectOptions('care_washing', CARE_WASHING_OPTIONS, t).map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="care_bleaching">{t('dresses.careBleachingField', 'Bleaching')}</label>
                <select
                  id="care_bleaching"
                  name="care_bleaching"
                  value={formData.care_bleaching}
                  onChange={handleInputChange}
                >
                  <option value="">{t('dresses.selectCareOption', '—')}</option>
                  {getCareSelectOptions('care_bleaching', CARE_BLEACHING_OPTIONS, t).map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="care_drying">{t('dresses.careDryingField', 'Drying')}</label>
                <select
                  id="care_drying"
                  name="care_drying"
                  value={formData.care_drying}
                  onChange={handleInputChange}
                >
                  <option value="">{t('dresses.selectCareOption', '—')}</option>
                  {getCareSelectOptions('care_drying', CARE_DRYING_OPTIONS, t).map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="care_ironing">{t('dresses.careIroningField', 'Ironing')}</label>
                <select
                  id="care_ironing"
                  name="care_ironing"
                  value={formData.care_ironing}
                  onChange={handleInputChange}
                >
                  <option value="">{t('dresses.selectCareOption', '—')}</option>
                  {getCareSelectOptions('care_ironing', CARE_IRONING_OPTIONS, t).map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="care_dry_cleaning">{t('dresses.careDryCleaningField', 'Dry cleaning')}</label>
                <select
                  id="care_dry_cleaning"
                  name="care_dry_cleaning"
                  value={formData.care_dry_cleaning}
                  onChange={handleInputChange}
                >
                  <option value="">{t('dresses.selectCareOption', '—')}</option>
                  {getCareSelectOptions('care_dry_cleaning', CARE_DRY_CLEANING_OPTIONS, t).map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notes">{t('dresses.notes')}</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder={t('dresses.dressNotes')}
              rows="3"
            />
          </div>

          {/* Change Log Button and Section - Only in edit mode */}
          {dress && (
            <>
              <div className="form-section">
                <div style={{ textAlign: 'center', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="sleek-button change-log"
                    onClick={handleToggleChangeLog}
                    disabled={isSubmitting}
                  >
                    {isChangeLogVisible ? t('reservations.hideChangeLog') : t('reservations.viewChangeLog')}
                  </button>
                </div>
              </div>

              <ChangeLog
                salonId={salonId}
                entityType="dress"
                entityId={dress?.id}
                getLocalizedFieldName={getLocalizedFieldName}
                isVisible={isChangeLogVisible}
                onToggle={handleToggleChangeLog}
                scrollContainerRef={modalBodyRef}
                titleKey="reservations.changeLogTitle"
                namespace="reservations"
              />
            </>
          )}
        </form>
        </div>
        
        <div className="modal-footer">
          <button type="button" className="salon-button secondary" onClick={handleClose}>
            {t('common.cancel')}
          </button>
          <button 
            type="submit" 
            className="salon-button primary"
            disabled={isSubmitting || userRole === 'member'}
            onClick={handleSubmit}
          >
            {isSubmitting ? `${t('common.save')}...` : (dress ? t('common.saveChanges') : t('dresses.addDress'))}
          </button>
        </div>
      </div>
      
      {/* Video Transcoding Modal */}
      <VideoTranscodingModal
        isOpen={isTranscoding}
        onClose={() => setIsTranscoding(false)}
        onCancel={cancelVideoTranscoding}
        progress={transcodingProgress}
        status={transcodingStatus}
        fileName={originalVideoFile?.name}
        error={transcodingError}
      />
      
      {/* Video2 Transcoding Modal */}
      <VideoTranscodingModal
        isOpen={isTranscoding2}
        onClose={() => setIsTranscoding2(false)}
        onCancel={cancelVideo2Transcoding}
        progress={transcodingProgress2}
        status={transcodingStatus2}
        fileName={originalVideoFile2?.name}
        error={transcodingError2}
      />
    </div>
  );
};

export default AddEditDressModal;

